// SalesProfitInvoice.jsx
import {
  message,
  Row,
  Col,
  Card,
  Space,
  Select,
  DatePicker,
  Button,
  Modal,
  Tooltip,
  Divider,
} from "antd";
import React, { useEffect, useState } from "react";
import API from "../../api";
import CustomTable from "../../components/Table/CustomTable";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import {
  SearchOutlined,
  EyeOutlined,
  ShopOutlined,
  CalendarOutlined,
  ReloadOutlined,
} from "@ant-design/icons";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const { Option } = Select;

const SalesProfitInvoice = () => {
  const [salesData, setSalesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stores, setStores] = useState([]);
  const [selectedStoreName, setSelectedStoreName] = useState(null);
  const [profitDetails, setProfitDetails] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [filters, setFilters] = useState({
    fromDate: null,
    toDate: null,
  });
  const [paginationInfo, setPaginationInfo] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [selectedFinancialYear, setSelectedFinancialYear] = useState(null);

  const userData = JSON.parse(localStorage.getItem("userData")) || {};
  const defaultStoreId = userData.storeId || null;

  const financialYears = (() => {
    const years = [];
    const currentYear = dayjs().year();
    for (let i = 0; i < 3; i++) {
      const start = dayjs(`${currentYear - i}-04-01`);
      const end = dayjs(`${currentYear - i + 1}-03-31`);
      years.push({
        label: `${start.format("YYYY")}-${end.format("YY")}`,
        value: `${start.format("YYYY-MM-DD")}_${end.format("YYYY-MM-DD")}`,
        start,
        end,
      });
    }
    return years;
  })();

  const fetchStores = async () => {
    try {
      const response = await API.get("/stores");
      setStores(response.data);
      const defaultStore = response.data.find(
        (store) => store._id === defaultStoreId
      );
      setSelectedStoreName(defaultStore ? defaultStore.name : null);
    } catch (error) {
      console.error("Error fetching stores:", error);
      message.error(
        error?.response?.data?.message || "Failed to fetch stores."
      );
    }
  };

  const fetchSalesData = async () => {
    try {
      setLoading(true);
      const selectedStore = selectedStoreName
        ? stores.find((store) => store.name === selectedStoreName)
        : null;
      const storeId = selectedStore ? selectedStore._id : null;

      const params = {
        storeId: storeId || undefined,
        fromDate: filters.fromDate
          ? dayjs(filters.fromDate).format("YYYY-MM-DD")
          : undefined,
        toDate: filters.toDate
          ? dayjs(filters.toDate).format("YYYY-MM-DD")
          : undefined,
        page: paginationInfo.current,
        limit: paginationInfo.pageSize,
      };

      const response = await API.get("/dashboard/getSalesProfit", { params });

      // Handle "No sales found" case
      if (
        response.data.error &&
        response.data.error.includes("No sales found")
      ) {
        setSalesData([]);
        setPaginationInfo((prev) => ({
          ...prev,
          total: 0,
        }));
        // Don't show error message, just set empty data
      } else {
        setSalesData(response.data.sales || []);
        setPaginationInfo((prev) => ({
          ...prev,
          total: response.data.pagination?.total || 0,
        }));
        if (response.data.message) {
          message.success(response.data.message);
        }
      }
    } catch (error) {
      console.error("Error fetching sales data:", error);
      // Only show error message for actual errors, not for "no data found"
      if (!error?.response?.data?.error?.includes("No sales found")) {
        message.error(
          error?.response?.data?.error || "Failed to fetch sales data."
        );
      }
      setSalesData([]);
      setPaginationInfo((prev) => ({
        ...prev,
        total: 0,
      }));
    } finally {
      setLoading(false);
    }
  };

  const showProfitDetails = (record) => {
    setProfitDetails({
      ...record.details,
      date: record.createdAt,
      store: record.storeName,
      invoiceId: record.invoiceId,
    });
    setIsModalVisible(true);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPaginationInfo((prev) => ({ ...prev, current: 1 }));
  };

  const handleReset = () => {
    setFilters({ fromDate: null, toDate: null });
    setSelectedStoreName(null);
    setSelectedFinancialYear(null);
    setPaginationInfo((prev) => ({ ...prev, current: 1 }));
    fetchSalesData();
  };

  useEffect(() => {
    fetchStores();
  }, []);

  useEffect(() => {
    if (stores.length > 0) {
      fetchSalesData();
    }
  }, [
    stores,
    selectedStoreName,
    filters,
    paginationInfo.current,
    paginationInfo.pageSize,
  ]);

  const salesColumns = [
    {
      title: "Sl. No",
      key: "slNo",
      render: (text, record, index) =>
        (paginationInfo.current - 1) * paginationInfo.pageSize + index + 1,
    },
    {
      title: "Invoice ID",
      dataIndex: "invoiceId",
      key: "invoiceId",
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => dayjs(date).format("DD-MM-YYYY"),
      sorter: (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
    },
    {
      title: "Profit",
      dataIndex: "profit",
      key: "profit",
      render: (profit) => (
        <span
          style={{
            color: profit <= 0 ? "#ff4d4f" : "#52c41a",
            fontWeight: "bold",
          }}
        >
          ₹{" "}
          {new Intl.NumberFormat("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(profit ?? 0)}
        </span>
      ),
      sorter: (a, b) => (a.profit || 0) - (b.profit || 0),
    },
    {
      title: "Store",
      dataIndex: "storeName",
      key: "storeName",
      render: (storeName) => (
        <span style={{ color: "#aaa", fontWeight: "bold" }}>{storeName}</span>
      ),
      sorter: (a, b) => (a.profit || 0) - (b.profit || 0),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Tooltip title="View Profit Details">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => showProfitDetails(record)}
            style={{ color: "#1890ff" }}
          />
        </Tooltip>
      ),
    },
  ];

  const profitColumns = [
    {
      title: "Product Name",
      dataIndex: "productName",
      key: "productName",
    },
    {
      title: "Avg Purchase Price",
      dataIndex: "avgPurchasePrice",
      key: "avgPurchasePrice",
      render: (price) =>
        `₹ ${new Intl.NumberFormat("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(price ?? 0)}`,
    },
    {
      title: "Avg Sale Price",
      dataIndex: "avgSalePrice",
      key: "avgSalePrice",
      render: (price) =>
        `₹ ${new Intl.NumberFormat("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(price ?? 0)}`,
    },
    {
      title: "Total Sold",
      dataIndex: "totalSold",
      key: "totalSold",
    },
    {
      title: "Total Profit",
      dataIndex: "totalProfit",
      key: "totalProfit",
      render: (profit) => (
        <span
          style={{
            color: profit <= 0 ? "#ff4d4f" : "#52c41a",
            fontWeight: "bold",
          }}
        >
          ₹{" "}
          {new Intl.NumberFormat("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(profit ?? 0)}
        </span>
      ),
      sorter: (a, b) => (a.totalProfit || 0) - (b.totalProfit || 0),
    },
  ];

  const totalProfit = salesData.reduce(
    (sum, item) => sum + (item.profit || 0),
    0
  );

  return (
    <div
      style={{
        padding: "20px",
        background: "#fff",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
      }}
    >
      <Row gutter={[16, 16]} style={{ marginBottom: "20px" }}>
        <Col xs={24}>
          <h2 style={{ fontSize: "24px", color: "#1a1a1a", margin: 0 }}>
            <ShopOutlined style={{ marginRight: "8px", color: "#1890ff" }} />
            Sales Profit Overview
          </h2>
        </Col>
      </Row>

      {/* Debug Information */}
      {process.env.NODE_ENV === "development" && (
        <Row
          gutter={[16, 16]}
          style={{
            marginBottom: "10px",
            padding: "10px",
            background: "#f0f0f0",
            borderRadius: "4px",
          }}
        >
          <Col xs={24}>
            <div style={{ fontSize: "12px", color: "#666" }}>
              <strong>Filtered Info:</strong> Selected Store:{" "}
              {selectedStoreName || "All Stores"} | Store ID:{" "}
              {selectedStoreName
                ? stores.find((s) => s.name === selectedStoreName)?._id
                : "N/A"}{" "}
              | Total Records: {salesData.length} | User:{" "}
              {userData.role || "N/A"}
            </div>
          </Col>
        </Row>
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: "20px" }}>
        <Col xs={24} sm={8} md={6}>
          <Select
            style={{ width: "100%" }}
            placeholder={
              <>
                <ShopOutlined /> Select a store
              </>
            }
            value={selectedStoreName || "all"}
            onChange={(value) => {
              setSelectedStoreName(value === "all" ? null : value);
              setPaginationInfo((prev) => ({ ...prev, current: 1 }));
            }}
            loading={loading}
            disabled={loading || userData.role !== "admin"}
            showSearch
            optionFilterProp="children"
          >
            <Option value="all">All Stores</Option>
            {stores.map((store) => (
              <Option key={store._id} value={store.name}>
                {store.name}
              </Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={8} md={6}>
          <DatePicker.RangePicker
            value={
              filters.fromDate && filters.toDate
                ? [dayjs(filters.fromDate), dayjs(filters.toDate)]
                : []
            }
            onChange={(dates) => {
              if (dates && dates.length === 2) {
                handleFilterChange("fromDate", dates[0].format("YYYY-MM-DD"));
                handleFilterChange("toDate", dates[1].format("YYYY-MM-DD"));
                setSelectedFinancialYear(null);
              } else {
                handleFilterChange("fromDate", null);
                handleFilterChange("toDate", null);
              }
            }}
            format="DD-MM-YYYY"
            style={{ width: "100%" }}
            placeholder={["Start Date", "End Date"]}
            prefix={<CalendarOutlined />}
          />
        </Col>
        <Col xs={24} sm={8} md={6}>
          <Select
            style={{ width: "100%" }}
            placeholder="Select Financial Year"
            value={selectedFinancialYear}
            onChange={(value) => {
              setSelectedFinancialYear(value);
              if (value === "all") {
                // Clear date filters when "All" is selected
                handleFilterChange("fromDate", null);
                handleFilterChange("toDate", null);
              } else {
                const fy = financialYears.find((fy) => fy.value === value);
                if (fy) {
                  handleFilterChange("fromDate", fy.start.format("YYYY-MM-DD"));
                  handleFilterChange("toDate", fy.end.format("YYYY-MM-DD"));
                }
              }
            }}
            allowClear
          >
            <Option value="all">All Financial Years</Option>
            {financialYears.map((fy) => (
              <Option key={fy.value} value={fy.value}>
                {fy.label}
              </Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={8} md={6}>
          <Space>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={fetchSalesData}
              loading={loading}
            >
              Filter
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleReset}
              disabled={loading}
            >
              Reset
            </Button>
          </Space>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} sm={12} md={8}>
          <div
            style={{
              background: "linear-gradient(135deg, #e6f7ff 0%, #91d5ff 100%)",
              padding: "18px 24px",
              borderRadius: "12px",
              color: "#1890ff",
              minWidth: "180px",
              textAlign: "center",
              boxShadow: "0 4px 12px rgba(145, 213, 255, 0.15)",
              border: "1px solid #91d5ff",
              fontWeight: 700,
              fontSize: 18,
            }}
          >
            <div style={{ fontSize: 14, opacity: 0.8, marginBottom: 4 }}>
              Total Profit
            </div>
            <div
              style={{
                fontSize: 22,
                color: totalProfit <= 0 ? "#ff4d4f" : "#1890ff",
              }}
            >
              ₹{" "}
              {new Intl.NumberFormat("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(totalProfit)}
            </div>
          </div>
        </Col>
      </Row>

      <CustomTable
        columns={salesColumns}
        dataSource={salesData}
        loading={loading}
        onPaginationChange={(pagination) => {
          setPaginationInfo((prev) => ({
            ...prev,
            current: pagination.current,
            pageSize: pagination.pageSize,
          }));
        }}
        total={paginationInfo.total}
        style={{ borderRadius: "8px", overflow: "hidden" }}
      />

      <Modal
        title={
          profitDetails ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                padding: "16px",
                background: "#fff",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                width: "100%",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap", // Ensure responsiveness
                  gap: "8px",
                }}
              >
                <span
                  style={{
                    fontSize: "16px",
                    fontWeight: "500",
                    color: "#1a1a1a",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <EyeOutlined
                    style={{
                      marginRight: "8px",
                      color: "#1890ff",
                      fontSize: "18px",
                    }}
                  />
                  Profit Details for Invoice #{profitDetails.invoiceId}
                </span>
                <span
                  style={{
                    fontSize: "14px",
                    color: "#595959",
                  }}
                >
                  Date:{" "}
                  {new Date(profitDetails.date).toISOString().split("T")[0]}
                </span>
              </div>
              <div
                style={{
                  fontSize: "14px",
                  color: "#595959",
                  textAlign: "end",
                }}
              >
                Store: {profitDetails.store}
              </div>
            </div>
          ) : (
            "Profit Details"
          )
        }
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={1000}
        bodyStyle={{ padding: "20px" }}
      >
        {profitDetails ? (
          <>
            <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
              <Col span={8}>
                <Card
                  title="Total Revenue"
                  style={{ background: "#f6ffed", borderColor: "#b7eb8f" }}
                >
                  <span style={{ fontSize: "18px", color: "#52c41a" }}>
                    ₹{" "}
                    {new Intl.NumberFormat("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(profitDetails.summary.totalRevenue ?? 0)}
                  </span>
                </Card>
              </Col>
              <Col span={8}>
                <Card
                  title="Total Cost"
                  style={{ background: "#fff1f0", borderColor: "#ffa39e" }}
                >
                  <span style={{ fontSize: "18px", color: "#f5222d" }}>
                    ₹{" "}
                    {new Intl.NumberFormat("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(profitDetails.summary.totalCost ?? 0)}
                  </span>
                </Card>
              </Col>
              <Col span={8}>
                <Card
                  title="Total Profit"
                  style={{ background: "#e6f7ff", borderColor: "#91d5ff" }}
                >
                  <span
                    style={{
                      fontSize: "18px",
                      color:
                        profitDetails.summary.totalProfit <= 0
                          ? "#f5222d"
                          : "#1890ff",
                      fontWeight: "bold",
                    }}
                  >
                    ₹{" "}
                    {new Intl.NumberFormat("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(profitDetails.summary.totalProfit ?? 0)}
                  </span>
                </Card>
              </Col>
            </Row>
            <CustomTable
              columns={profitColumns}
              dataSource={profitDetails.products}
              pagination={false}
              style={{ borderRadius: "8px", overflow: "hidden" }}
            />
            <div
              style={{ marginTop: 16, textAlign: "right", fontSize: "14px" }}
            >
              <p>
                <strong>Total Sold Amount:</strong> ₹{" "}
                {new Intl.NumberFormat("en-IN", {
                  minimumFractionDigits: 2,
                }).format(
                  profitDetails.summary.PaymentCollection.totalAmount ?? 0
                )}
              </p>
              <p>
                <strong>Amount Collected:</strong> ₹{" "}
                {new Intl.NumberFormat("en-IN", {
                  minimumFractionDigits: 2,
                }).format(
                  profitDetails.summary.PaymentCollection.paidAmount ?? 0
                )}
              </p>
              <p>
                <strong>Amount Remaining:</strong>{" "}
                <span
                  style={{
                    color:
                      profitDetails.summary.PaymentCollection.balanceAmount > 0
                        ? "#ff4d4f"
                        : "#52c41a",
                  }}
                >
                  ₹{" "}
                  {new Intl.NumberFormat("en-IN", {
                    minimumFractionDigits: 2,
                  }).format(
                    profitDetails.summary.PaymentCollection.balanceAmount ?? 0
                  )}
                </span>
              </p>
            </div>
          </>
        ) : (
          <p style={{ textAlign: "center", color: "#8c8c8c" }}>
            No profit details available
          </p>
        )}
      </Modal>
    </div>
  );
};

export default SalesProfitInvoice;
